/* eslint-disable react/prop-types */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { isPdfUrl } from "../helpers";

/**
 * The prescription itself, held on screen for the whole review.
 *
 * When a medicine row is selected its bounding box is drawn over the image and
 * scrolled into view, so the pharmacist can check the machine's reading against
 * the doctor's handwriting without leaving the list.
 */
const RxViewer = ({ pages = [], activePage = 0, onPageChange, boundingBox }) => {
    const frameRef = useRef(null);
    const imageRef = useRef(null);
    const [rendered, setRendered] = useState(null);

    const measure = useCallback(() => {
        const img = imageRef.current;
        if (!img || !img.naturalWidth) return;
        setRendered({
            width: img.clientWidth,
            height: img.clientHeight,
            naturalWidth: img.naturalWidth,
            naturalHeight: img.naturalHeight,
        });
    }, []);

    useEffect(() => {
        window.addEventListener("resize", measure);
        return () => window.removeEventListener("resize", measure);
    }, [measure]);

    // Bring the highlighted line into view when the selection changes.
    useEffect(() => {
        if (!boundingBox || !rendered || !frameRef.current) return;

        const scale = rendered.width / rendered.naturalWidth;
        const top = boundingBox.ymin * scale;
        const frame = frameRef.current;
        const target = Math.max(0, top - frame.clientHeight / 2 + ((boundingBox.ymax - boundingBox.ymin) * scale) / 2);

        frame.scrollTo({
            top: target,
            behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth",
        });
    }, [boundingBox, rendered]);

    const current = pages[activePage];
    if (!current) return null;

    const isPdf = isPdfUrl(current);

    const scale = rendered ? rendered.width / rendered.naturalWidth : 0;
    const highlight = boundingBox && scale
        ? {
            left: boundingBox.xmin * scale,
            top: boundingBox.ymin * scale,
            width: (boundingBox.xmax - boundingBox.xmin) * scale,
            height: (boundingBox.ymax - boundingBox.ymin) * scale,
        }
        : null;

    return (
        <div className="flex flex-col gap-3">
            <div className="flex items-baseline justify-between">
                <h2 className="text-[11px] font-semibold uppercase tracking-[0.12em] text-cod-gray-400">
                    Prescription
                </h2>
                {pages.length > 1 && (
                    <div className="flex items-center gap-1">
                        {pages.map((page, index) => (
                            <button
                                key={page}
                                type="button"
                                onClick={() => onPageChange(index)}
                                aria-label={`Page ${index + 1}`}
                                aria-current={index === activePage}
                                className={`h-6 w-6 rounded text-[11px] font-semibold tabular-nums transition-colors ${index === activePage
                                    ? "bg-cod-gray-900 text-white-50"
                                    : "bg-cod-gray-100 text-cod-gray-500 hover:bg-cod-gray-200"
                                    }`}
                            >
                                {index + 1}
                            </button>
                        ))}
                    </div>
                )}
            </div>

            {isPdf ? (
                // Azure serves the blob as application/pdf, so the browser's own
                // viewer renders it in-frame. No bounding-box overlay for PDFs —
                // OCR coordinates are only meaningful against a raster image.
                <div className="flex flex-col gap-2">
                    <iframe
                        src={current}
                        title="Uploaded prescription"
                        className="h-[calc(100dvh-21rem)] w-full rounded-lg border border-cod-gray-100 bg-white-50"
                    />
                    <a
                        href={current}
                        target="_blank"
                        rel="noreferrer"
                        className="self-end text-xs font-medium text-elm-600 hover:underline"
                    >
                        Open in new tab
                    </a>
                </div>
            ) : (
                <div
                    ref={frameRef}
                    className="relative max-h-[calc(100dvh-19rem)] overflow-y-auto rounded-lg border border-cod-gray-100 bg-white-50"
                >
                    <div className="relative">
                        <img
                            ref={imageRef}
                            src={current}
                            onLoad={measure}
                            alt="Uploaded prescription"
                            className="w-full"
                        />
                        {highlight && (
                            <span
                                aria-hidden="true"
                                style={highlight}
                                className="pointer-events-none absolute rounded-sm bg-amber-300/30 ring-2 ring-amber-400 transition-all duration-300 motion-reduce:transition-none"
                            />
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default RxViewer;
